import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  FileText,
  Search,
} from "lucide-react";
import Reveal from "./Reveal.jsx";

const examples = [
  {
    id: "quotes",
    label: "Spreadsheets → software",
    title: "One place for the work. Not another spreadsheet.",
    before:
      "Quotes in one file. Customer details in another. Updates that depend on someone remembering to send them.",
    after:
      "A shared application for customers, quotes, approvals, and the next step. Built around your process.",
    link: "/software",
    caption: "CUSTOMER & QUOTING WORKSPACE",
  },
  {
    id: "forms",
    label: "PDFs → digital workflows",
    title: "The form is just the beginning.",
    before:
      "Download a PDF. Fill it in. Email it back. Enter the same information into another system.",
    after:
      "Complete the form once. Store the information in a database, check what’s missing, and send it to the right person.",
    link: "/software",
    caption: "DIGITAL FORMS & REVIEW",
  },
  {
    id: "website",
    label: "Static website → connected business",
    title: "Update the data. The website follows.",
    before:
      "Product details copied across pages. Changes made by hand. Little visibility into what customers need.",
    after:
      "A searchable product catalog connected to your data, with filters, inquiry tools, and analytics that respect consent.",
    link: "/software",
    caption: "CONNECTED PRODUCT WEBSITE",
  },
];

function Workspace({ type }) {
  if (type === "quotes")
    return (
      <>
        <div className="kw-example-window-title">
          <span>Northstar / Sales workspace</span>
          <span className="kw-example-avatar">AL</span>
        </div>
        <div className="kw-example-screen-heading">
          <div>
            <small>CUSTOMERS & QUOTES</small>
            <h3>A clear next step.</h3>
          </div>
          <span className="kw-example-status">Review queue</span>
        </div>
        <div
          className="kw-example-table"
          role="table"
          aria-label="Illustrative quote list"
        >
          <div role="row">
            <span role="columnheader">Customer / quote</span>
            <span role="columnheader">Owner</span>
            <span role="columnheader">Status</span>
          </div>
          {[
            [
              "Alder Manufacturing",
              "Equipment package · Q-1042",
              "Alex",
              "Needs approval",
            ],
            [
              "Cedar Supply",
              "Service renewal · Q-1041",
              "Jordan",
              "Ready to send",
            ],
            ["Harbor Studio", "Installation · Q-1040", "Alex", "Draft"],
          ].map(([company, quote, owner, status]) => (
            <div role="row" key={company}>
              <span role="cell">
                <strong>{company}</strong>
                <small>{quote}</small>
              </span>
              <span role="cell">{owner}</span>
              <span role="cell">
                <em>{status}</em>
              </span>
            </div>
          ))}
        </div>
        <div className="kw-example-handoff">
          <Check size={17} />
          <div>
            <strong>Pricing checked. Approval next.</strong>
            <span>
              The reviewer has the quote and supporting details together.
            </span>
          </div>
        </div>
      </>
    );
  if (type === "forms")
    return (
      <>
        <div className="kw-example-window-title">
          <span>Northstar / Operations</span>
          <span className="kw-example-avatar">JM</span>
        </div>
        <div className="kw-example-screen-heading">
          <div>
            <small>SERVICE REQUEST / SR-208</small>
            <h3>Enter it once.</h3>
          </div>
          <span className="kw-example-status">In review</span>
        </div>
        <div className="kw-example-form-fields">
          {[
            ["Company", "Alder Manufacturing"],
            ["Site", "North facility"],
            ["Requested service", "Equipment inspection"],
            ["Preferred date", "18 November"],
          ].map(([label, value]) => (
            <div key={label}>
              <small>{label}</small>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
        <div className="kw-example-attachment">
          <FileText size={19} />
          <div>
            <strong>Site requirements.pdf</strong>
            <span>Supporting document attached</span>
          </div>
          <Check size={16} />
        </div>
        <div className="kw-example-handoff">
          <Check size={17} />
          <div>
            <strong>Complete. Saved. Routed for review.</strong>
            <span>
              A record your team can find, update, and follow through.
            </span>
          </div>
        </div>
      </>
    );
  return (
    <>
      <div className="kw-example-window-title">
        <span>Northstar / Product catalog</span>
        <span className="kw-example-avatar">n.</span>
      </div>
      <div className="kw-example-screen-heading">
        <div>
          <small>PRODUCTS & SOLUTIONS</small>
          <h3>Find the right fit.</h3>
        </div>
      </div>
      <div className="kw-example-search">
        <Search size={16} />
        <span>Search products, specifications, or applications</span>
      </div>
      <div className="kw-example-catalog">
        <div className="kw-example-filters">
          <small>APPLICATION</small>
          <span className="is-selected">All products</span>
          <span>Monitoring</span>
          <span>Control</span>
          <small>AVAILABILITY</small>
          <span>Available to quote</span>
        </div>
        <div className="kw-example-products">
          {[
            ["S-200", "Monitoring sensor", "Temperature · Indoor"],
            ["C-100", "Control module", "Networked · Compact"],
          ].map(([model, name, spec], i) => (
            <div key={model}>
              <div
                className={`kw-product-object kw-product-object--${i}`}
                aria-hidden="true"
              >
                <span />
                <span />
                <span />
              </div>
              <small>{model}</small>
              <strong>{name}</strong>
              <span>{spec}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="kw-example-handoff">
        <Check size={17} />
        <div>
          <strong>One source of product information.</strong>
          <span>
            Catalog pages and inquiry details use the same company data.
          </span>
        </div>
      </div>
    </>
  );
}

export default function BusinessExamples() {
  const [selected, setSelected] = useState(0);
  const example = examples[selected];
  return (
    <section
      id="examples"
      className="kw-real-examples"
      aria-labelledby="examples-title"
    >
      <Reveal className="kw-real-examples-heading">
        <span className="kw-small-label">RECOGNIZE ANY OF THIS?</span>
        <h2 id="examples-title">
          There’s a better way
          <br />
          to run the everyday.
        </h2>
        <p>
          Your company already has a way of working. The opportunity is in the
          copying, chasing, searching, and manual updates around it.
        </p>
      </Reveal>
      <div
        className="kw-example-selectors"
        role="group"
        aria-label="Explore business software examples"
      >
        {examples.map((item, index) => (
          <button
            type="button"
            key={item.id}
            aria-pressed={selected === index}
            aria-controls="business-example"
            onClick={() => setSelected(index)}
            data-analytics={`example_${item.id}`}
          >
            {item.label}
            <ArrowUpRight size={15} />
          </button>
        ))}
      </div>
      <div id="business-example" className="kw-example-chapter">
        <div className="kw-example-explanation">
          <h3>{example.title}</h3>
          <div>
            <small>THE FRICTION</small>
            <p>{example.before}</p>
          </div>
          <div>
            <small>WHAT CHANGES</small>
            <p>{example.after}</p>
          </div>
          <Link
            to={example.link}
            data-analytics={`example_explore_${example.id}`}
          >
            Explore Software <ArrowRight size={16} />
          </Link>
        </div>
        <div className="kw-real-workspace">
          <div className="kw-example-caption">
            <span>{example.caption}</span>
            <span>Illustrative example · Fictional data</span>
          </div>
          <div className="kw-example-screen" key={example.id}>
            <Workspace type={example.id} />
          </div>
        </div>
      </div>
      <div className="kw-example-private">
        <p>
          This is what the possibilities can look like. Your business is where
          the conversation starts.
        </p>
        <Link to="/contact" data-analytics="examples_discovery">
          Let’s talk <ArrowUpRight size={17} />
        </Link>
      </div>
    </section>
  );
}
